/**
 * useProductSelectorData
 * Fetches locations and groups product_inventory_items by product+variant.
 * Also queries po_creation_items to find how many cases of each lot
 * are already committed across all active PI orders.
 */
import { useState, useCallback } from 'react';
import { supabase } from '../../../../../supabaseClient';

export function useProductSelectorData(companyId) {
  const [locations, setLocations] = useState([]);
  const [groupedLots, setGroupedLots] = useState([]);
  const [committedCases, setCommittedCases] = useState({}); // { inventoryItemId: totalCases }
  const [lotLoading, setLotLoading] = useState(false);

  const fetchLocations = useCallback(async () => {
    if (!companyId) return;
    try {
      const { data } = await supabase
        .from('locations')
        .select('id, name, is_main')
        .eq('company_id', companyId)
        .eq('is_active', true)
        .order('name');
      setLocations(data || []);
    } catch (err) {
      console.error('Error fetching locations:', err);
    }
  }, [companyId]);

  const fetchLotsForLocation = useCallback(async (locationId) => {
    if (!companyId || !locationId) {
      setGroupedLots([]);
      setCommittedCases({});
      return;
    }
    setLotLoading(true);
    try {
      // Fetch inventory lots
      const { data, error } = await supabase
        .from('product_inventory_items')
        .select(`
          id, product_id, variant_id, lot_number, batch_number,
          manufactured_date, expiry_date,
          total_base_units, units_per_case, cases_per_pallet,
          enable_cases, enable_pallets, location_id,
          products:product_id(id, name, sku),
          product_variants:variant_id(id, name, sku, price, unit_price)
        `)
        .eq('company_id', companyId)
        .eq('location_id', locationId)
        .eq('is_active', true)
        .gt('total_base_units', 0)
        .order('expiry_date', { ascending: true, nullsFirst: false });

      if (error) throw error;

      const lots = data || [];

      // Fetch committed quantities from all active PI orders for these lots.
      // Normalize everything to base units so Cases and Units can be compared correctly.
      let committed = {}; // { lotId: totalBaseUnits }
      if (lots.length > 0) {
        const lotIds = lots.map(l => l.id);
        // Build a map of lotId -> units_per_case for normalization
        const lotUpcMap = {};
        for (const lot of lots) {
          lotUpcMap[lot.id] = lot.units_per_case || 1;
        }

        // Exclude POs whose inventory has already been deducted — those lots'
        // total_base_units already reflect the deduction, so counting them again
        // in "In Other Orders" would double-count.
        const { data: deductedConfirmations } = await supabase
          .from('po_delivery_confirmations')
          .select('purchase_order_id')
          .eq('company_id', companyId)
          .eq('inventory_deducted', true);
        const deductedPoIds = (deductedConfirmations || []).map(d => d.purchase_order_id).filter(Boolean);

        let piItemsQuery = supabase
          .from('po_creation_items')
          .select('inventory_item_id, quantity, unit_type')
          .in('inventory_item_id', lotIds)
          .eq('line_status', 'active');
        if (deductedPoIds.length > 0) {
          piItemsQuery = piItemsQuery.not('purchase_order_id', 'in', `(${deductedPoIds.join(',')})`);
        }
        const { data: piItems } = await piItemsQuery;

        for (const item of piItems || []) {
          if (!item.inventory_item_id) continue;
          const upc = lotUpcMap[item.inventory_item_id] || 1;
          // Convert to base units: Cases * upc, Units stay as-is
          const baseUnits = item.unit_type === 'Unit'
            ? (item.quantity || 0)
            : (item.quantity || 0) * upc;
          committed[item.inventory_item_id] = (committed[item.inventory_item_id] || 0) + baseUnits;
        }
      }
      setCommittedCases(committed); // values are now base units

      // Group by product_id + variant_id
      const groups = {};
      for (const lot of lots) {
        const key = `${lot.product_id}_${lot.variant_id || 'null'}`;
        if (!groups[key]) {
          const pName = lot.products?.name || 'Unknown';
          const vName = lot.product_variants?.name || null;
          groups[key] = {
            key,
            product_id: lot.product_id,
            variant_id: lot.variant_id || null,
            productName: pName,
            variantName: vName,
            displayName: vName ? `${pName} — ${vName}` : pName,
            sku: lot.product_variants?.sku || lot.products?.sku || '',
            casePrice: parseFloat(lot.product_variants?.price) || 0,
            unitPrice: parseFloat(lot.product_variants?.unit_price) || 0,
            lots: [],
          };
        }
        groups[key].lots.push(lot);
      }

      // Sort alpha by product name, then by variant size descending
      const extractSize = (s) => {
        const m = (s || '').match(/(\d+(\.\d+)?)/);
        return m ? parseFloat(m[1]) : 0;
      };
      const sorted = Object.values(groups).sort((a, b) => {
        const na = a.productName.toLowerCase();
        const nb = b.productName.toLowerCase();
        if (na !== nb) return na.localeCompare(nb);
        return extractSize(b.variantName) - extractSize(a.variantName);
      });

      setGroupedLots(sorted);
    } catch (err) {
      console.error('Error fetching inventory lots:', err);
      setGroupedLots([]);
      setCommittedCases({});
    } finally {
      setLotLoading(false);
    }
  }, [companyId]);

  return { locations, groupedLots, committedCases, lotLoading, fetchLocations, fetchLotsForLocation };
}
